import { Transition } from '@headlessui/react';
import SearchNearbyPois from 'components/search/SearchNearbyPois';
import SearchResults from 'components/search/SearchResults';
import SearchContext from 'contexts/SearchContext';
import { buildApiQuery, buildParamsQuery, parseUrlQuery } from 'contexts/SearchHelpers';
import { Filter as FilterIcon, VBlizini } from 'kzv-icons';
import { useRouter } from 'next/router';
import React, { Fragment, useContext, useEffect, useState } from 'react';
import useSWR from 'swr';

import IconButton from '../buttons/IconButton';
import MobileSearchFilters from './Filters/MobileSearchFilters';
import PoiTypeFilters from './Filters/PoiTypeFilters';
import SearchFilters from './Filters/SearchFilters';
import MapViewToggle from './MapViewToggle';
import NoResults from './NoResults';
import ResultsSkeletonLoader from './ResultsSkeletonLoader';
import SearchMap from './SearchMap';
import SearchPagination from './SearchPagination';
import SearchSort from 'components/search/SearchSort';
import {
    initialise,
    setArea,
    setAvailableFilters,
    setAvailableSorts,
    setFilters,
    setLoading,
    setMapView,
    setPage,
    setResults,
    setSort,
    setSortDirection
} from 'contexts/SearchActions';
import AutocompleteSearch from 'components/search/autocomplete/AutocompleteSearch';
import SearchCategories from 'components/search/SearchCategories';
import ClearAllFiltersButton from 'components/search/ClearAllFiltersButton';
import useClientLocation from 'hooks/useClientLocation';
import Button from 'components/buttons/Button';
import LoadingSpinner from 'components/LoadingSpinner';
import LocationNoPermission from 'components/search/LocationNoPermission';

function SearchPage() {
    const [mobileFiltersOpened, setMobileFiltersOpened] = useState(false);
    const { state, dispatch } = useContext(SearchContext);
    const { clientLocation, requestClientLocation, storedPermission } = useClientLocation();
    const router = useRouter();

    // if (typeof window !== 'undefined' && window.location.hostname === 'localhost') console.log('search state', state);

    const { area, category, currentPage, filters, sort, sortDirection, perPage, results } = state;

    const apiQuery = buildApiQuery({
        area,
        category,
        currentPage,
        filters,
        sort,
        sortDirection,
        perPage,
        clientLocation
    });

    const { data, isValidating: isFetchingData } = useSWR(`/public/search?${apiQuery}`);
    const paramsQuery = buildParamsQuery({ area, category });
    const { data: searchParams } = useSWR(`/public/search-params?${paramsQuery}`);

    useEffect(() => {
        setLoading(dispatch, isFetchingData);
    }, [isFetchingData]);

    useEffect(() => {
        if (window.scrollY > 100) window.scrollTo(0, 100);
    }, [currentPage]);

    useEffect(() => {
        if (data) {
            setResults(dispatch, data);
        }
    }, [data]);

    useEffect(() => {
        if (searchParams) {
            setAvailableFilters(dispatch, searchParams.data.filters);
            setAvailableSorts(dispatch, searchParams.data.sorts);
        }
    }, [searchParams]);

    useEffect(() => {
        if (state.url && typeof window !== 'undefined') {
            if (state.url === router.asPath) return;

            const host = window.location.origin;
            const updatedPath = new URL(host + state.url).pathname;
            const currentPath = new URL(host + router.asPath).pathname;

            // If base path changed, do a deep routing, displaying prerendered page for interest or area
            // If only URL query changed do a shallow route, without triggering Next.js getStaticProps methods.
            if (updatedPath !== currentPath) {
                router.push(state.url, undefined, { shallow: true, scroll: false });
            } else {
                router.push(state.url, undefined, { shallow: true, scroll: false });
            }
        }
    }, [state.url]);

    // Set initial state according to URL parameters
    // And listen to url changes
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const { filters, sort, currentPage, mapView, sortDirection } = parseUrlQuery({
            filters: urlParams.get('filters'),
            sort: urlParams.get('sort'),
            sortDirection: urlParams.get('sortDirection'),
            page: urlParams.get('page'),
            map: urlParams.get('map')
        });
        setFilters(dispatch, filters);
        setSort(dispatch, sort);
        setPage(dispatch, currentPage);
        setMapView(dispatch, mapView);
        setSortDirection(dispatch, sortDirection);

        if (mapView) window.scrollTo(0, 100);

        initialise(dispatch);
    }, [router.asPath]);

    return (
        <div className="search-interface pb-32">
            <section id="autocomplete" className="pt-6 px-4">
                <AutocompleteSearch
                    initQuery={state.area?.name}
                    handleReset={() => setArea(dispatch, null)}
                    placeholder={'Poišči izletno točko, regijo ali kraj ...'}
                />
            </section>

            <section id="categories">
                <SearchCategories />
            </section>

            <section id="types">
                <PoiTypeFilters />
            </section>

            {(results?.data?.length > 0 || filters.length > 0) && (
                <section
                    id="filtri"
                    className="container mt-4 grid grid-cols-2 md:grid-cols-3 items-center md:items-end justify-center"
                >
                    <div className={`${filters.length > 0 || category ? 'visible' : 'invisible'}`}>
                        <ClearAllFiltersButton />
                    </div>
                    <div className="results hidden md:block text-center">
                        <span className="text-sm text-gray-500">{results?.meta?.total} zadetkov</span>
                    </div>
                    <div className="filters-sorts flex gap-3 justify-end">
                        <div className="hidden sm:block">
                            <SearchFilters />
                        </div>
                        <div className="sort flex gap-1">
                            <SearchSort />
                        </div>
                    </div>
                </section>
            )}

            {state.sort?.value === 'nearby' &&
                !(clientLocation.latitude || clientLocation.longitude) &&
                !storedPermission && (
                    <section id="request-location-permission" className="mt-8 mb-16">
                        <div className="container text-center px-6">
                            {clientLocation.error && <LocationNoPermission />}
                            {!clientLocation.error && (
                                <>
                                    <h2 className="text-brand">Potrebujemo dostop do tvoje lokacije</h2>
                                    <p className="mt-4 mb-6 max-w-lg mx-auto">
                                        Da bi lahko prikazali izletne ideje v tvoji bližini, potrebujemo dovoljenje za
                                        dostop do tvoje lokacije.
                                    </p>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        endIcon={
                                            clientLocation.loading ? (
                                                <LoadingSpinner width={'1em'} height={'1em'} />
                                            ) : (
                                                <VBlizini strokeWidth={1.5} />
                                            )
                                        }
                                        onClick={requestClientLocation}
                                        disabled={clientLocation.loading}
                                    >
                                        Dovoli uporabo lokacije
                                    </Button>
                                </>
                            )}
                        </div>
                    </section>
                )}

            {!state.mapView && (
                <>
                    <section id="results" className={`mt-4 ${state.isLoading ? 'opacity-50' : ''}`}>
                        {!results?.data && <ResultsSkeletonLoader />}
                        {results?.data?.length > 0 && (
                            <div className="container">
                                <SearchResults pois={state.results.data} category={category} />
                            </div>
                        )}
                        {results?.data?.length === 0 && (
                            <div className="mt-8">
                                <NoResults area={area} category={category} filters={state.filters} />
                                {area && <SearchNearbyPois area={area} category={category} />}
                            </div>
                        )}
                        {/* If we have only one page, show nearby ideas, excluding listed ideas */}
                        {results?.data?.length > 0 && results?.meta.last_page === 1 && area && (
                            <div className="mt-8">
                                <SearchNearbyPois
                                    area={area}
                                    category={category}
                                    exclude={results.data.map((p) => p.id)}
                                />
                            </div>
                        )}
                    </section>
                    <section id="pagination" className="mt-8">
                        {results?.data && !state.isLoading && <SearchPagination />}
                    </section>
                </>
            )}
            {state.mapView && (
                <section id="zemljevid" className="h-[calc(100vh-150px)]">
                    <SearchMap />
                </section>
            )}

            {results?.data?.length > 0 && (
                <section
                    id="sticky-bottom-nav"
                    className="fixed bottom-0 w-full z-10 md:bottom-2 md:text-lg pointer-events-none"
                >
                    <div className="container flex space-x-2 items-center py-4 justify-center">
                        <MapViewToggle variant="contained" />
                        <div className="md:hidden pointer-events-auto inline-block">
                            <IconButton
                                styles={`bg-white border-gray-300 border shadow-2xl ${
                                    filters.length > 0 || sort?.value ? 'border-brand border' : ''
                                } `}
                                onClick={() => setMobileFiltersOpened(true)}
                                disabled={false}
                            >
                                <FilterIcon width="1.2rem" height="1.2rem" strokeWidth={1.3} />
                            </IconButton>
                        </div>
                    </div>
                </section>
            )}

            <Transition
                show={mobileFiltersOpened}
                as={Fragment}
                enter="transition ease-out duration-300"
                enterFrom="transform translate-y-full"
                enterTo="transform translate-y-0"
            >
                <div className="fixed inset-0  z-[110] bg-white">
                    <MobileSearchFilters handleClose={() => setMobileFiltersOpened(false)} />
                </div>
            </Transition>
        </div>
    );
}

export default SearchPage;
